import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraSpinner, SpecraText } from '@mono/ui-components/ensigns';
import { Testids } from '@mono/ui-selectors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useChatScroll } from '../../hooks/useChatScroll';
import { bareChatId, useChatHistory } from '../../hooks/useConversation';
import { useShanksChatConnect } from '../../hooks/useShanksChatConnect';
import { ChatHistoryUnavailable, ChatTurnError } from '../ChatView/ChatRecovery';
import { ChatThread } from '../ChatView/ChatThread';
import { convertShanksToChatMessages, mergeMessagesById } from '../ChatView/shanksMessageAdapter';
import { ComposerInput } from '../ComposerInput';
import { useOfficeWorkspaceAssist } from './OfficeWorkspaceAssistContext';
import { buildQuickActions, QuickActions } from './QuickActions';
export function assistIntro(unitNoun) {
    return `Assist works on this ${unitNoun} only, with what you can see, including your private notes. It labels hearsay as unverified, and it may paraphrase hearsay, customer conversations and black-hat notes but never quotes them. Its edits arrive as tracked changes for you to accept, recorded as yours via Specra Assist.`;
}
const NO_ANCHOR = 'document';
export function assistConversationKey(documentId, sectionAnchor) {
    return `officeWorkspace.assistConversation.${documentId}.${sectionAnchor || NO_ANCHOR}`;
}
function readConversation(key) {
    try {
        const id = window.localStorage.getItem(key);
        if (id) return {
            id,
            existed: true
        };
    } catch  {
    // Blocked storage: a fresh conversation each mount.
    }
    const id = crypto.randomUUID();
    writeConversation(key, id);
    return {
        id,
        existed: false
    };
}
function writeConversation(key, id) {
    try {
        window.localStorage.setItem(key, id);
    } catch  {
    // Not remembered.
    }
}
export function buildAssistEnvelope(p) {
    return {
        surface: p.surface,
        projectId: p.projectId,
        volumeId: p.volumeId,
        rfiResponseProjectId: p.rfiResponseProjectId,
        documentId: p.documentId,
        sectionAnchor: p.sectionAnchor || NO_ANCHOR,
        responseContainer: p.responseContainer
    };
}
/** The docked Assist tab (WRT-11). Remounts per (document, section) so each has its own conversation. */ export function AssistRail(props) {
    const key = assistConversationKey(props.documentId, props.sectionAnchor);
    return /*#__PURE__*/ _jsx(AssistChat, {
        ...props,
        storageKey: key
    }, key);
}
function AssistChat(props) {
    const { storageKey, canEdit, unitNoun } = props;
    const assist = useOfficeWorkspaceAssist();
    const [conv, setConv] = useState(()=>readConversation(storageKey));
    const [started, setStarted] = useState(conv.existed);
    const chatId = conv.id;
    const { scrollRef, jumpToLatest } = useChatScroll(chatId);
    const history = useChatHistory(chatId, !started);
    const { data: historyData, isLoading: historyLoading } = history;
    const readOnly = !canEdit || !!historyData?.readOnly;
    const { surface, projectId, volumeId, rfiResponseProjectId, documentId, sectionAnchor } = props;
    const { responseContainer } = props;
    const envelope = useMemo(()=>buildAssistEnvelope({
            surface,
            projectId,
            volumeId,
            rfiResponseProjectId,
            documentId,
            sectionAnchor,
            responseContainer
        }), [
        surface,
        projectId,
        volumeId,
        rfiResponseProjectId,
        documentId,
        sectionAnchor,
        responseContainer
    ]);
    const savedSubmission = historyData?.submissions?.find((s)=>s.state === 'running') ?? historyData?.submissions?.[0];
    const restoredSubmission = useMemo(()=>savedSubmission?.message ? {
            id: savedSubmission.submissionId,
            content: savedSubmission.message,
            fileIds: savedSubmission.fileIds,
            state: savedSubmission.state
        } : undefined, [
        savedSubmission
    ]);
    const shanks = useShanksChatConnect({
        chatId,
        initialConversationId: bareChatId(chatId),
        projectId: props.projectId,
        context: envelope,
        onConversationId: (id)=>{
            setStarted(true);
            // After "New conversation" the server mints the id; remember that one.
            if (id && bareChatId(id) !== bareChatId(chatId)) {
                writeConversation(storageKey, id);
                setConv({
                    id,
                    existed: true
                });
            }
        },
        onFinish: ()=>void history.refetch(),
        onError: ()=>void history.refetch(),
        restoredSubmission
    });
    const busy = shanks.isLoading || !readOnly && savedSubmission?.state === 'running';
    const unfinished = !readOnly && savedSubmission && [
        'incomplete',
        'failed',
        'interrupted'
    ].includes(savedSubmission.state);
    const incomplete = shanks.incomplete || !shanks.error && unfinished && savedSubmission.state === 'incomplete';
    const turnError = busy ? null : shanks.error ?? (unfinished ? incomplete ? 'This request needs more work. Your progress is saved. Continue to finish it.' : 'This response was interrupted. Retry your saved request.' : null);
    const historyMessages = useMemo(()=>historyData?.messages ?? [], [
        historyData
    ]);
    const liveMessages = useMemo(()=>convertShanksToChatMessages(shanks.messages), [
        shanks.messages
    ]);
    const messages = useMemo(()=>mergeMessagesById(historyMessages, liveMessages, shanks.isLoading), [
        historyMessages,
        liveMessages,
        shanks.isLoading
    ]);
    const canSend = !readOnly && !historyLoading && !history.isError && !busy;
    const send = useCallback(async (prompt, fileIds)=>{
        const content = prompt.trim();
        if (!content || !canSend) return;
        await shanks.sendMessage(content, {
            fileIds
        });
        jumpToLatest();
    }, [
        shanks,
        canSend,
        jumpToLatest
    ]);
    // "Ask Assist to fix": the event fills the composer; the user sends it.
    const [draft, setDraft] = useState();
    const hasPrefill = !!assist?.prefill;
    const consumePrefill = assist?.consumePrefill;
    useEffect(()=>{
        if (!hasPrefill || !consumePrefill) return;
        const p = consumePrefill();
        if (p) setDraft({
            text: p.prompt
        });
    }, [
        hasPrefill,
        consumePrefill
    ]);
    const newConversation = ()=>{
        shanks.reset();
        const id = crypto.randomUUID();
        writeConversation(storageKey, id);
        setStarted(false);
        setConv({
            id,
            existed: false
        });
    };
    const actions = useMemo(()=>buildQuickActions({
            ...props.quickActionData,
            unit: props.surface === 'rfi_response' ? 'rfi' : 'proposal',
            sectionAnchor: props.sectionAnchor
        }), [
        props.quickActionData,
        props.surface,
        props.sectionAnchor
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex h-full min-h-0 flex-col gap-[var(--space-3)] p-[var(--space-3)]",
        "data-testid": Testids.officeWorkspace.assistRail,
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                children: assistIntro(unitNoun)
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": Testids.officeWorkspace.newConversation,
                    onClick: newConversation,
                    size: "xs",
                    variant: "ghost",
                    children: "New conversation"
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "min-h-0 flex-1 overflow-y-auto",
                ref: scrollRef,
                children: [
                    historyLoading ? /*#__PURE__*/ _jsx(SpecraSpinner, {
                        label: "Loading conversation"
                    }) : history.isError ? /*#__PURE__*/ _jsx(ChatHistoryUnavailable, {
                        error: history.error,
                        onRetry: ()=>void history.refetch()
                    }) : /*#__PURE__*/ _jsx(ChatThread, {
                        chatId: chatId,
                        isSending: busy,
                        messages: messages,
                        onFeedback: (m)=>void send(m),
                        readOnly: readOnly
                    }),
                    turnError ? /*#__PURE__*/ _jsx(ChatTurnError, {
                        error: turnError,
                        incomplete: incomplete,
                        onRetry: ()=>void shanks.retry()
                    }) : null
                ]
            }),
            !history.isError && !readOnly ? /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col gap-[var(--space-2)]",
                children: [
                    /*#__PURE__*/ _jsx(QuickActions, {
                        actions: actions,
                        busy: busy,
                        onSend: (p)=>void send(p)
                    }),
                    /*#__PURE__*/ _jsx(ComposerInput, {
                        draft: draft,
                        mode: "chat",
                        onStop: shanks.stop,
                        onSubmit: (d)=>send(d.prompt, d.attachments?.map((a)=>a.id)),
                        submitLabel: "Send",
                        submitting: busy
                    })
                ]
            }) : null
        ]
    });
}
